import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome, faTags, faShoppingCart, faClock, faFileAlt, faHeart, faBell, faStar, faUserFriends,
  faCog, faUser, faLock, faLifeRing, faShieldAlt, faPlus, faChartBar, faEnvelopeOpenText, faBullhorn,
  faMoneyBillWave, faUsers, faBuilding, faCheckToSlot
} from '@fortawesome/free-solid-svg-icons';

const SidebarMenu = ({ selectedRole, onSelectPage }) => {
  const [openMenus, setOpenMenus] = useState({});
  const toggleSubMenu = (menu) => setOpenMenus(prev => ({ ...prev, [menu]: !prev[menu] }));

  const MenuItem = ({ icon, label, page }) => (
    <li
      onClick={() => onSelectPage(page, label)}
      className="cursor-pointer p-2 hover:bg-gray-600 text-white flex items-center gap-2"
    >
      <FontAwesomeIcon icon={icon} /> {label}
    </li>
  );

  const SubMenu = ({ label, icon, children }) => (
    <div>
      <div
        onClick={() => toggleSubMenu(label)}
        className="cursor-pointer p-2 hover:bg-gray-400 flex items-center justify-between"
      >
        <span className="flex items-center gap-2"><FontAwesomeIcon icon={icon} /> {label}</span>
        <span>{openMenus[label] ? '▲' : '▼'}</span>
      </div>
      {openMenus[label] && (
        <ul className="ml-4 border-l border-gray-300 pl-2">
          {children}
        </ul>
      )}
    </div>
  );

  const renderCustomerMenu = () => (
    <>
      <MenuItem icon={faTags} label="Browse Deals" page="browse-deals" />
      <SubMenu label="My Purchases" icon={faShoppingCart}>
        <MenuItem icon={faShoppingCart} label="Active Deals" page="active-purchases" />
        <MenuItem icon={faClock} label="Expired Deals" page="expired-purchases" />
        <MenuItem icon={faFileAlt} label="Voucher History" page="voucher-history" />
      </SubMenu>
      <MenuItem icon={faHeart} label="Favorites" page="favorites" />
      <MenuItem icon={faBell} label="Notifications" page="notifications" />
      <MenuItem icon={faStar} label="Reviews" page="reviews" />
      <MenuItem icon={faUserFriends} label="Referral & Loyalty" page="referral-loyalty" />
      <SubMenu label="Settings" icon={faCog}>
        <MenuItem icon={faUser} label="Profile" page="profile" />
        <MenuItem icon={faLock} label="Account Security" page="account-security" />
        <MenuItem icon={faCog} label="Preferences" page="preferences" />
      </SubMenu>
      <MenuItem icon={faLifeRing} label="Support" page="support" />
    </>
  );

  const renderPartnerMenu = () => (
    <>
      <MenuItem icon={faShieldAlt} label="My Profile" page="business-profile" />
      <SubMenu label="Deal Center" icon={faTags}>
        <MenuItem icon={faPlus} label="Submit New Deal" page="submit-deal" />
        <MenuItem icon={faTags} label="Manage Deals" page="manage-deals" />
        <MenuItem icon={faChartBar} label="Performance Analytics" page="deal-performance" />
      </SubMenu>
      <SubMenu label="Customer Engagement" icon={faEnvelopeOpenText}>
        <MenuItem icon={faEnvelopeOpenText} label="Inquiries" page="customer-inquiries" />
        <MenuItem icon={faStar} label="Respond to Reviews" page="respond-reviews" />
        <MenuItem icon={faBullhorn} label="Send Offers" page="send-offers" />
      </SubMenu>
      <SubMenu label="Analytics" icon={faChartBar}>
        <MenuItem icon={faChartBar} label="Sales Reports" page="sales-reports" />
        <MenuItem icon={faChartBar} label="Redemption Metrics" page="redemption-metrics" />
        <MenuItem icon={faUsers} label="Customer Insights" page="customer-insights" />
      </SubMenu>
      <SubMenu label="Earnings" icon={faMoneyBillWave}>
        <MenuItem icon={faMoneyBillWave} label="Payment History" page="payment-history" />
        <MenuItem icon={faCog} label="Payment Settings" page="payment-settings" />
      </SubMenu>
      <SubMenu label="Settings" icon={faCog}>
        <MenuItem icon={faBuilding} label="Business Info" page="business-info" />
        <MenuItem icon={faUsers} label="Team Access" page="team-access" />
        <MenuItem icon={faLock} label="Security" page="business-security" />
      </SubMenu>
      <MenuItem icon={faLifeRing} label="Support" page="business-support" />
    </>
  );

  const renderAdminMenu = () => (
    <>
      <SubMenu label="Manage Users" icon={faUsers}>
        <MenuItem icon={faUsers} label="View Consumers" page="admin-users" />
        <MenuItem icon={faUserFriends} label="Flags & Issues" page="user-flags" />
      </SubMenu>
      <SubMenu label="Manage Businesses" icon={faBuilding}>
        <MenuItem icon={faBuilding} label="Pending Vendors" page="pending-vendors" />
        <MenuItem icon={faBuilding} label="Approved Vendors" page="approved-vendors" />
        <MenuItem icon={faChartBar} label="Performance" page="vendor-performance" />
      </SubMenu>
      <SubMenu label="Deal Moderation" icon={faTags}>
        <MenuItem icon={faCheckToSlot} label="Approve/Reject" page="moderate-deals" />
        <MenuItem icon={faFileAlt} label="Expired Logs" page="expired-logs" />
      </SubMenu>
      <SubMenu label="Marketing Tools" icon={faBullhorn}>
        <MenuItem icon={faEnvelopeOpenText} label="Email Campaigns" page="email-campaigns" />
        <MenuItem icon={faBell} label="Push Notifications" page="push-notifications" />
        <MenuItem icon={faTags} label="Featured Deals" page="featured-deals" />
      </SubMenu>
      <SubMenu label="Analytics & Reports" icon={faChartBar}>
        <MenuItem icon={faChartBar} label="Platform Stats" page="platform-stats" />
        <MenuItem icon={faChartBar} label="User Trends" page="user-trends" />
        <MenuItem icon={faMoneyBillWave} label="Revenue Reports" page="revenue-reports" />
      </SubMenu>
      <SubMenu label="Settings" icon={faCog}>
        <MenuItem icon={faCog} label="Platform Config" page="platform-config" />
        <MenuItem icon={faLock} label="Security Logs" page="security-logs" />
        <MenuItem icon={faFileAlt} label="Data Compliance" page="data-compliance" />
      </SubMenu>
      <MenuItem icon={faLifeRing} label="Support" page="admin-support" />
    </>
  );

  const renderMenuByRole = () => {
    switch (selectedRole) {
      case 'Customer': return renderCustomerMenu();
      case 'Partner': return renderPartnerMenu();
      case 'Admin': return renderAdminMenu();
      default: return null;
    }
  };

  return (
    <div className="flex flex-col w-full sm:w-2/5 md:w-1/4 lg:w-1/5 bg-blue-500 text-white border-2 border-blue-300 shadow-md h-screen overflow-y-auto">
     <ul className="p-2">
        <MenuItem icon={faHome} label={`${selectedRole} Dashboard`} page="dashboard" />
        {renderMenuByRole()}
      </ul>
    </div>
  );
};

export default SidebarMenu;
